import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  lazy,
} from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import { Layout } from "@/common/layout";
import { Row, Col, Card, Drawer, Pagination } from "antd";
import "@/common/table-scroll-optimizations.less";
import { DashboardTabs } from "@/common/dashboardTabs";
import { RightTabs } from "@/common/rightTabs";
import SubTabButtons from "@/pages/dashboard/dashboard.main.subTabPanel";
import DocumentFiltersContainer from "@/common/document-filters/document-filters-component";
import FilterComponent from "@/pages/dashboard/dashboard.main.filterPanel";
import TableComponent from "@/pages/dashboard/dashboard.main.tablePanel";
import { CartFilter } from "@/pages/cart-management/cart.lazy";
import DashboardTableActions from "@/common/document-filters/select-footer";
import PageSizeSelector from "@/common/page-size-selector";
//Panel imports
import panels from "@/constants/panels";
import { FormProvider } from "@/common/form-context";
import PanelContainer from "@/common/panel";
import PanelLazyLoader from "@/common/panel-lazy-loader";
import DocumentCommentPanel from "@/pages/dashboard/dashboard-panels/dashboard.detail.comment";
import UnderChangeComment from "@/pages/dashboard/dashboard-panels/under-change-comment";
import UpdateCategoriesPanel from "@/pages/dashboard/dashboard-panels/update-categories";
import ChangeSitesPanel from "@/pages/dashboard/dashboard-panels/change-site";
// Panels below render inside PanelLazyLoader's Suspense boundary, so they load on first open.
const CreateJobSummary = lazy(() =>
  import("@/pages/job-summary/create-job-summary")
);
const UserProfile = lazy(() =>
  import("@/common/user-profile/user-profile")
);
const RetireDocumentPanel = lazy(() =>
  import("@/pages/dashboard/dashboard-panels/retire-document-panel")
);
const DeleteDocumentPanel = lazy(() =>
  import("@/pages/dashboard/dashboard-panels/delete-document-panel")
);
const RollbackConfirm = lazy(() =>
  import("@/pages/dashboard/dashboard-panels/rollback-confirm-modal")
);
const CreateWorkpackPanel = lazy(() =>
  import("@/pages/dashboard/dashboard-panels/create-workpack-panel")
);
const DocumentDetailPage = lazy(() =>
  import("@/pages/dashboard/document-detail/document.detail.page")
);
const PreviewPdf = lazy(() => import("@/common/preview-pdf"));

import {
  resetColumns as resetColumnsAction,
  setCurrentPage as setCurrentPageAction,
  setLargeTableView as setLargeTableViewAction,
  setValue,
  setThumbnailTableView as setThumbnailTableViewAction,
  setColumns as setColumnsAction,
  setCurrentDashboardName as setCurrentDashboardNameAction,
  selectCurrentDashboardFields,
  updateDocumentCounts
} from "@/pages/dashboard/dashboard.slice";
import {
  setFilter as setFilterAction,
  setSelectedTableItems,
  setAllTableSiteId,
} from "@/pages/shared/shared.reducer";
import { useNavigationItems } from "@/common/navigation/navigation.items";
import get from "lodash.get";
import {
  loadColumnPrefs,
  onReorder,
  setEnabledColumns,
  reconcileColumnsWithStorage,
} from "@/pages/dashboard/index.utils";
import {
  dashboardApi,
  useGetTableDataQuery,
} from "@/pages/dashboard/dashboard.reducer";
import { getDashboardFilters } from "@/common/document-filters/document-filters-reducer";
import { selectDashboardCartItems } from "@/pages/cart-management/cart-management.slice";
import { updateDashboardDocumentCounts } from "@/pages/dashboard/dashboard.counts";
import {
  isDocumentMetadataImportForSite,
  subscribeToDocumentMetadataImported,
} from "@/common/document-metadata-import-events";

const EMPTY_OBJECT = {};

const NO_ITEMS = [];

const Dashboard = () => {
  const dispatch = useDispatch();
  const store = useStore();

  const currentSiteId = useSelector((state) => state.shared.siteId);
  const permissions = useSelector((state) => state.userDetails.permissions);
  const defaultSiteId = useSelector((state) =>
    get(state.userDetails, "setting.defaultSiteId")
  );
  const selectedDashboardItems = useSelector(
    (state) => get(state.shared, "dashboardTable.selectedItems") || NO_ITEMS
  );
  const selectedCartItems = useSelector(
    (state) => get(state.shared, "cartManagementTable.selectedItems") || NO_ITEMS
  );
  const showAllRevision = useSelector(
    (state) =>
      get(state.shared, "dashboardTable.filters.showAllRevision") || false
  );
  const filterDrawerOpen = useSelector((state) =>
    get(state.dashboardPage, "filterDrawerOpen")
  );

  const clearSelectedItems = useCallback(
    (table) => {
      dispatch(setSelectedTableItems(table, [], []));
    },
    [dispatch]
  );

  const setFilter = useCallback(
    (table = "dashboardTable", name, value) => {
      dispatch(setFilterAction(table, name, value));
    },
    [dispatch]
  );

  const setFilterDrawerVisible = useCallback(
    (value) => {
      dispatch(setValue({ key: "filterDrawerOpen", value }));
    },
    [dispatch]
  );

  const setCurrentSiteId = useCallback(
    (id) => {
      dispatch(setAllTableSiteId(id));
      dispatch(setFilterAction("dashboardTable", "siteId", id));
    },
    [dispatch]
  );

  const setCurrentDashboardName = useCallback(
    (dashboardName) => {
      dispatch(setCurrentDashboardNameAction(dashboardName));
    },
    [dispatch]
  );

  const setColumns = useCallback(
    (value) => {
      dispatch(setColumnsAction({ tableId: "documentDashboard", value }));
    },
    [dispatch]
  );

  const setLargeTableView = useCallback(
    (isCart) => {
      dispatch(
        setFilterAction(
          isCart ? "cartManagementTable" : "dashboardTable",
          "includeThumbnail",
          false
        )
      );
      dispatch(
        setLargeTableViewAction(isCart ? "cartDashboard" : "documentDashboard")
      );
    },
    [dispatch]
  );

  const setThumbnailTableView = useCallback(
    (isCart) => {
      dispatch(
        setFilterAction(
          isCart ? "cartManagementTable" : "dashboardTable",
          "includeThumbnail",
          true
        )
      );
      dispatch(
        setThumbnailTableViewAction(
          isCart ? "cartDashboard" : "documentDashboard"
        )
      );
    },
    [dispatch]
  );

  const setCurrentPage = useCallback(
    (isCart, value) => {
      dispatch(
        setCurrentPageAction(
          isCart ? "cartManagementTable" : "dashboardTable",
          value
        )
      );
    },
    [dispatch]
  );

  const resetColumns = useCallback(
    (key) => {
      dispatch(resetColumnsAction(key));
    },
    [dispatch]
  );

  const [activeTabKey, setActiveTabKey] = useState("1");
  const [width, setWidth] = useState(1920);
  const [mobileView, setMobileView] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(true);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [ddvSearch, setDdvSearch] = useState(
    () => store.getState().shared?.dashboardTable?.filters?.documentNumber || ""
  );
  const [descriptionSearch, setDescriptionSearch] = useState(
    () => store.getState().shared?.dashboardTable?.filters?.keywords || ""
  );
  const [cartCountLoading, setCartCountLoading] = useState(false);
  const storageKeyRef = useRef("dashboardTable.columns.large");
  const tableRef = useRef(null);
  const filterRef = useRef(null);
  const searchBarRef = useRef(null);
  const filterActionRef = useRef(null);
  const rightTabsRef = useRef(null);
  const tableActionsRef = useRef(null);
  const prevViewRef = useRef({ isLargeView: false });
  const hasInitializedCartCountRef = useRef(false);
  const pendingCartCountRequestsRef = useRef(0);
  const previousAccountIdRef = useRef(null);

  const isDocumentsTab = useMemo(() => activeTabKey === "1", [activeTabKey]);
  const isCartTab = useMemo(() => activeTabKey === "2", [activeTabKey]);
  const isFavouriteTab = useMemo(() => activeTabKey === "3", [activeTabKey]);

  const [documentTabCount, setDocumentTabCount] = useState(0);

  const pdfPreviewDocNum = useSelector(
    (state) => state.dashboardPage[panels.pdfPreview]?.item.documentNumber || ""
  );
  const isTourOpen = useSelector((state) => state.dashboardPage.isTourOpen);
  const currentDashboard = useSelector(
    (state) => state.dashboardPage.currentDashboard
  );
  const sites = useSelector((state) => state.dashboardPage.sites);
  const item = useSelector((state) => state.dashboardPage.item);
  const { documentNumber } = useSelector((state) => state.dashboardPage.item);

  const cartDocumentIds = useSelector(selectDashboardCartItems);

  const sharedTableState = useSelector((state) =>
    state.shared[isCartTab ? "cartManagementTable" : "dashboardTable"] || EMPTY_OBJECT
  );
  const pageSize = sharedTableState.filters.pageSize || 150;
  const currentPage = sharedTableState.filters.currentPage || 1;

  const {
    sideView,
    isLargeView,
    isThumbnailView,
    columns,
  } = useSelector(selectCurrentDashboardFields);

  const currentSite = useMemo(
    () =>
      sites.find((site) => site.siteId == currentSiteId) ||
      sites.find((site) => site.siteId == defaultSiteId) ||
      sites[0] ||
      null,
    [currentSiteId, defaultSiteId, sites]
  );
  const currentAccountId = currentSite?.accountId ?? null;
  const resolvedSiteId = currentSite?.siteId ?? currentSiteId;
  const combinedFilters = useMemo(() => {
    const filters = {
      ...sharedTableState.filters,
      siteId: resolvedSiteId,
      cartDocumentIds: isCartTab ? cartDocumentIds : null,
      cartDocumentName: isCartTab ? "dashboardCartDocument" : null,
      favouritesOnly: isFavouriteTab,
    };
    return filters;
  }, [
    sharedTableState.filters,
    resolvedSiteId,
    cartDocumentIds,
    isFavouriteTab,
    isCartTab,
  ]);

  const {
    data: tableData,
    refetch,
    isLoading,
    isFetching,
  } = useGetTableDataQuery(combinedFilters, { refetchOnMountOrArgChange: true });

  const documentCounts = useSelector(
    (state) => state.dashboardPage.documentCounts
  );

  const dashboardTableCount = tableData?.totalItems;
  const siteTotalCount = tableData?.siteTotalItems || 0;
  const allSiteDocumentCount = documentCounts.allItems || 0;
  const favouriteTableCount = documentCounts.favourites || 0;
  const cartTableCount = documentCounts.cartItems || 0;
  const tableId = isCartTab ? "cartManagementTable" : "dashboardTable"

  if (isDocumentsTab && dashboardTableCount !== undefined && dashboardTableCount !== documentTabCount) {
    setDocumentTabCount(dashboardTableCount);
  }

  useEffect(() => {
    if (!currentAccountId) {
      previousAccountIdRef.current = currentAccountId;
      return;
    }

    if (previousAccountIdRef.current == null) {
      previousAccountIdRef.current = currentAccountId;
      return;
    }

    if (previousAccountIdRef.current !== currentAccountId) {
      previousAccountIdRef.current = currentAccountId;
      dispatch(
        dashboardApi.endpoints.fetchAccountDashboardSettings.initiate(undefined, {
          subscribe: false,
          forceRefetch: true,
        })
      );
      return;
    }

    previousAccountIdRef.current = currentAccountId;
  }, [currentAccountId, dispatch]);

  const tableCount = {
    1: isDocumentsTab
      ? dashboardTableCount
      : documentTabCount,
    2: cartTableCount,
    3: dashboardTableCount,
  }

  const selectedItems = useMemo(
      () => (isCartTab ? selectedCartItems : selectedDashboardItems) || [],
      [selectedCartItems, selectedDashboardItems, isCartTab]
    );

  useEffect(() => {
    document.title = "Main Dashboard";
  }, []);
  const handleWindowResize = useCallback(() => setWidth(window.innerWidth), []);

  useEffect(() => {
    const adjustLayoutForWidth = () => {
      if (width <= 1440) {
        setShowFilterDrawer(true);
        setShowFilterPanel(false);
      } else {
        setShowFilterDrawer(false);
        setShowFilterPanel(true);
      }
      setMobileView(width < 768);
    };

    const updateDimensions = () => {
      handleWindowResize();
      adjustLayoutForWidth();
    };

    window.addEventListener("resize", updateDimensions);
    updateDimensions();

    return () => window.removeEventListener("resize", updateDimensions);
  }, [width, handleWindowResize]);

  const handleUpdateDocumentsCount = useCallback(async () => {
    try {
      const result = await dispatch(
        dashboardApi.endpoints.getDocumentsCount.initiate(
          resolvedSiteId,
          { forceRefetch: true }
        )
      ).unwrap();
      dispatch(updateDocumentCounts({ allItems: result }));
    } catch (error) {
      console.error("Error fetching document count:", error);
    }
  }, [dispatch, resolvedSiteId]);

  const handleUpdateCartCount = useCallback(async () => {
    pendingCartCountRequestsRef.current += 1;
    setCartCountLoading(true);

    try {
      const cartShowAllRevision = sharedTableState.filters?.showAllRevision || false;
      const result = await dispatch(
        dashboardApi.endpoints.getCartDocumentsCount.initiate(
          {
            siteId: resolvedSiteId,
            cartDocumentIds: cartDocumentIds,
            cartDocumentName: "dashboardCartDocuments",
            showAllRevision: cartShowAllRevision,
          },
          { forceRefetch: true }
        )
      ).unwrap();
      dispatch(updateDocumentCounts({ cartItems: result }));
    } catch (error) {
      console.error("Error fetching cart document count:", error);
    } finally {
      pendingCartCountRequestsRef.current = Math.max(0, pendingCartCountRequestsRef.current - 1);

      if (pendingCartCountRequestsRef.current === 0) {
        setCartCountLoading(false);
      }
    }
  }, [dispatch, resolvedSiteId, cartDocumentIds, sharedTableState.filters]);

  const handleUpdateFavouriteCount = useCallback(async () => {
    try {
      const result = await dispatch(
        dashboardApi.endpoints.getFavouriteDocumentsCount.initiate(
          resolvedSiteId,
          { forceRefetch: true }
        )
      ).unwrap();
      dispatch(updateDocumentCounts({ favourites: result }));
    } catch (error) {
      console.error("Error fetching favourite document count:", error);
    }
  }, [dispatch, resolvedSiteId]);

  const updateTotalDocumentCount = useCallback(async () => {
    await updateDashboardDocumentCounts({
      updateCartCount: handleUpdateCartCount,
      updateFavouriteCount: handleUpdateFavouriteCount,
      updateDocumentsCount: handleUpdateDocumentsCount,
    });
  }, [
    handleUpdateCartCount,
    handleUpdateFavouriteCount,
    handleUpdateDocumentsCount,
  ]);

  useEffect(() => {
    updateTotalDocumentCount();
  }, [updateTotalDocumentCount]);

  useEffect(() => {
    if (!hasInitializedCartCountRef.current) {
      hasInitializedCartCountRef.current = true;
      return;
    }

    handleUpdateCartCount();
  }, [cartDocumentIds, handleUpdateCartCount]);

  const handleSearch = useCallback(
    (newDdvSearch = "", newDescriptionSearch = "") => {
      const tableId = isCartTab ? "cartManagementTable" : "dashboardTable";
      setFilter(tableId, "documentNumber", newDdvSearch);
      setFilter(tableId, "vendorNumber", newDdvSearch);
      setFilter(tableId, "keywords", newDescriptionSearch);
    },
    [setFilter, isCartTab]
  );

  const resetSearchValues = useCallback(() => {
    const tableId = isCartTab ? "cartManagementTable" : "dashboardTable";
    // Clear shared local state
    setDdvSearch("");
    setDescriptionSearch("");
    // Clear Redux filters
    setFilter(tableId, "documentNumber", "");
    setFilter(tableId, "vendorNumber", "");
    setFilter(tableId, "keywords", "");
  }, [setFilter, isCartTab, setDdvSearch, setDescriptionSearch]);

  const handleSubTabsChange = useCallback(
    (value) => {
      setActiveTabKey(value);
      clearSelectedItems("cartManagementTable");
      clearSelectedItems("dashboardTable");
      resetSearchValues();
      if (value === "1") {
        setCurrentDashboardName("documentDashboard");
      } else if (value === "2") {
        setCurrentDashboardName("cartDashboard");
      } else if (value === "3") {
        setCurrentDashboardName("documentDashboard");
      }
    },
    [
      clearSelectedItems,
      setCurrentDashboardName,
      resetSearchValues,
    ]
  );

  useEffect(() => {
    setCurrentSiteId(
      !currentSiteId || String(currentSiteId) === "0" ? defaultSiteId : currentSiteId
    );
  }, [defaultSiteId, setCurrentSiteId, currentSiteId]);

  useEffect(() => {
    if (isCartTab && cartDocumentIds.length > 0) {
      refetch();
    }
  }, [isCartTab, cartDocumentIds, combinedFilters, refetch]);

  useEffect(() => {
    return subscribeToDocumentMetadataImported((message) => {
      if (!isDocumentMetadataImportForSite(message, resolvedSiteId)) {
        return;
      }

      dispatch(getDashboardFilters(combinedFilters, tableId, true));
      refetch();
    });
  }, [combinedFilters, dispatch, refetch, resolvedSiteId, tableId]);


  const handleFilterDrawerVisible = useCallback(
    (value) => {
      setFilterDrawerVisible(value);
      setShowFilterDrawer(value);
    },
    [setFilterDrawerVisible, setShowFilterDrawer]
  );

  const columnOptions = useMemo(
    () =>
      columns.length > 0
        ? columns.filter(
            (column) =>
              !(
                column.dataIndex == "documentNumber" ||
                column.dataIndex == "fixed-hover-right"
              )
          )
        : [],
    [columns]
  );

  const storageKey = useMemo(() => {
    if (isLargeView) return "dashboardTable.columns.large";
    return "";
  }, [isLargeView]);

  useEffect(() => {
    if (prevViewRef.current.isLargeView === isLargeView) {
      return;
    }

    prevViewRef.current = { isLargeView };

    if (storageKey) {
      storageKeyRef.current = storageKey;
      const savedColumnPrefs = loadColumnPrefs(storageKey);

      if (savedColumnPrefs.hasStored) {
        reconcileColumnsWithStorage(columns, setColumns, storageKey);
      } else {
        const keysToEnable = columns
          .filter(
            (col) =>
              col.dataIndex !== "documentNumber" &&
              col.dataIndex !== "fixed-hover-right"
          )
          .map((col) => col.key);
        setEnabledColumns(keysToEnable, columns, setColumns, storageKey);
      }
    }
  }, [isLargeView, setColumns, columns, storageKey]);

  const triggerColumnsUpdate = useCallback(
    (item) => {
      if (storageKey) {
        setEnabledColumns(item, columns, setColumns, storageKey);
      }
    },
    [columns, setColumns, storageKey]
  );

  const handleReorderColumns = useCallback(
    (item) => {
      if (storageKey) {
        onReorder(item, columns, setColumns, storageKey);
      }
    },
    [columns, setColumns, storageKey]
  );

  const handleResetColumns = useCallback(() => {
    if (storageKey) {
      resetColumns(storageKey);
    }
  }, [resetColumns, storageKey]);

  const getDashboardTableScrollHeight = useCallback(() => {
    const holder = document.querySelector(
      ".dashboard-native-scroll-panel .ant-table-tbody-virtual-holder"
    );
    const footer = document.querySelector(".dashboard-table-footer");

    if (!holder || !footer) {
      return null;
    }

    const availableHeight =
      footer.getBoundingClientRect().top - holder.getBoundingClientRect().top;

    return availableHeight > 0 ? Math.ceil(availableHeight) : null;
  }, []);

  const getDashboardTableHeight = useCallback(() => {
    const measuredHeight = getDashboardTableScrollHeight();
    if (measuredHeight != null) {
      return measuredHeight;
    }

    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    if (windowWidth >= 1912) {
      return windowHeight - 294;
    } else if (windowWidth >= 1441 && windowWidth < 1912) {
      return windowHeight - 310;
    } else if (windowWidth >= 1024 && windowWidth <= 1440) {
      return windowHeight - 350;
    } else if (windowWidth >= 768 && windowWidth <= 1023) {
      return windowHeight - 375;
    } else if (windowWidth >= 360) {
      return windowHeight - 390;
    } else {
      return windowHeight - 418;
    }
  }, [getDashboardTableScrollHeight]);

  const getTopViewDashboardTableHeight = useCallback(() => {
    const measuredHeight = getDashboardTableScrollHeight();
    if (measuredHeight != null) {
      return measuredHeight;
    }

    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    if (windowWidth >= 1912) {
      return windowHeight - 500;
    } else {
      return windowHeight - 650;
    }
  }, [getDashboardTableScrollHeight]);

  const subTabsOptions = [
    {
      key: "1",
      label: "Results",
      count: isDocumentsTab ? dashboardTableCount : documentTabCount,
      icon: "ri-folder-3-line",
    },
    {
      key: "2",
      label: "Cart",
      count: cartTableCount,
      icon: "ri-shopping-basket-line",
    },
    {
      key: "3",
      label: "Favourites",
      count: favouriteTableCount,
      icon: "ri-star-line",
    },
  ];

  const steps = useMemo(
    () => [
      {
        title: "Main Dashboard Table",
        description: "All the stored documents / drawings are shown here. Select a row by clicking in the table or checkbox. Tools will appear only when a document is selected. Adjust column width and table views as you prefer.",
        arrow: true,
        target: () => tableRef.current,
      },
      {
        title: "Cart and Favourites Tabs",
        description: "Switch between Cart and Favourites here. You can add documents to your cart and favourites from the main dashboard.",
        target: () => rightTabsRef.current,
      },
      {
        title: "Table Actions",
        description: "Download, Add to cart, Add to favourites, Retire, Delete, Change sites and Update categories for selected documents here.",
        target: () => tableActionsRef.current,
      },
      {
        title: "Search Bar",
        description:
          "Search for document/drawing number, vendor number or description here. Press enter or select apply. Reset will remove all search criteria.",
        target: () => searchBarRef.current,
      },
      {
        title: "Dashboard Filter",
        description: "Breakdown your search via Site, Categories and Filters here.",
        target: () => filterRef.current,
      },
      {
        title: "Dashboard Filter Actions",
        description: "Pressing enter or clicking apply filters here will refine your document search. Reset will remove the information from the top and side search fields.",
        target: () => filterActionRef.current,
      },
    ],
    []
  );

  const navitems = useNavigationItems();
  const dashboardItems = navitems.find((item) => item.title === "Dashboards");

  useEffect(() => {
    document.title = "Main Dashboard";
    setCurrentDashboardName("documentDashboard");
  }, [setCurrentDashboardName]);

  return (
    <Layout steps={steps} currentDashboard={currentDashboard}>
      <div className={`${isCartTab ? "cart-table" : "dashboard-content"} dashboard-table-container`}>
        <Row gutter={[16, 16]}>
          <DashboardTabs
            style={{ width: "100%" }}
            dashboardItems={dashboardItems}
            showFilterPanel={showFilterPanel}
            setShowFilterPanel={setShowFilterPanel}
            setActiveTabKey={handleSubTabsChange}
            subTabActiveKey={activeTabKey}
            rightTabs={
              <RightTabs
                rightTabsRef={rightTabsRef}
                activeTabKey={activeTabKey}
                onChange={handleSubTabsChange}
                cartCountLoading={cartCountLoading}
                tabCounts={{
                  2: cartTableCount,
                  3: favouriteTableCount,
                }}
              />
            }
          >
            <Col
              span={sideView && showFilterPanel ? 19 : 24}
              className="dashboard-document-tab-content"
            >
              <div
                className={width <= 1440
                        ? "table-container" : sideView ? "table-container" : "top-view-table"}
                // className={"top-view-table"}
              >
                {!sideView ? (
                  <Row
                    style={{
                      marginBottom: width <= 1440 ? "0px" : "16px",
                      border: "1px solid #DADCE0",
                      borderRadius: "8px",
                      display: showFilterPanel ? "block" : "none",
                    }}
                  >
                    <Card className="top-filter-card">
                      <FilterComponent
                        updateTotalDocumentCount={updateTotalDocumentCount}
                        isDocumentsTab={isDocumentsTab}
                        isCartTab={isCartTab}
                        showFilterPanel={showFilterPanel}
                        filterActionRef={!sideView ? filterActionRef : null}
                        //props={{ ddvSearch, descriptionSearch }}
                        resetSearchValues={resetSearchValues}
                        handleFilterSearch={handleSearch}
                        mobileView={mobileView}
                        showFilterDrawer={showFilterDrawer}
                        selectedItems={selectedItems}
                        activeTabKey={activeTabKey}
                        pageSize={pageSize}
                        columnOptions={columnOptions}
                        selectedColumns={columnOptions?.filter(
                          (x) => x.isEnabled
                        )}
                        tableCount={tableCount[activeTabKey] || 0}
                        ddvSearch={ddvSearch}
                        descriptionSearch={descriptionSearch}
                        setDdvSearch={setDdvSearch}
                        setDescriptionSearch={setDescriptionSearch}
                        setEnabledColumns={setEnabledColumns}
                        onColumnClearment={handleResetColumns}
                        onReorder={onReorder}
                        isThumbnailView={isThumbnailView}
                        tableViewVisible={true}
                        setLargeTableView={setLargeTableView}
                        setThumbnailTableView={setThumbnailTableView}
                        width={width}
                        altSearch={descriptionSearch}
                        setAltSearch={setDescriptionSearch}
                        isShowColumnsSelector={true}
                        closeFilterDrawer={() =>
                          handleFilterDrawerVisible(false)
                        }
                        tableId={tableId}
                      />
                    </Card>
                  </Row>
                ) : null}
                <Card
                  style={{
                    overflow: "hidden",
                    height: width <= 1440
                      ? "100%"
                      : sideView || !showFilterPanel
                      ? "100%"
                      : "calc(100vh - 355px)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  className="dashboard-table-card"
                >
                  <div
                    className={`dashboard-table-container dashboard-table-responsive ${
                      width <= 1440
                        ? "filters-side"
                        : sideView || !showFilterPanel
                        ? "filters-side"
                        : "filters-above"
                    }`}
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                    }}
                  >
                    <SubTabButtons
                      activeTabKey={activeTabKey}
                      handleSubTabsChange={handleSubTabsChange}
                      totalCount={tableCount[activeTabKey]}
                      siteTotalCount={siteTotalCount}
                      ddvSearch={ddvSearch}
                      descriptionSearch={descriptionSearch}
                      setDdvSearch={setDdvSearch}
                      setDescriptionSearch={setDescriptionSearch}
                      handleSearch={handleSearch}
                      handleFilterDrawerVisible={handleFilterDrawerVisible}
                      pageSize={pageSize}
                      setLargeTableView={setLargeTableView}
                      setThumbnailTableView={setThumbnailTableView}
                      mobileView={mobileView}
                      isLargeView={isLargeView}
                      isThumbnailView={isThumbnailView}
                      isCartTab={isCartTab}
                      isDocumentsTab={isDocumentsTab}
                      width={width}
                      columnOptions={columnOptions}
                      setEnabledColumns={triggerColumnsUpdate}
                      onColumnClearment={handleResetColumns}
                      onReorder={handleReorderColumns}
                      selectedColumns={columnOptions?.filter(
                        (x) => x.isEnabled
                      )}
                      subTabsOptions={subTabsOptions}
                      tableViewVisible={true}
                      isShowColumnsSelector={true}
                      closeFiltersDrawer={() =>
                        handleFilterDrawerVisible(false)
                      }
                      searchBarRef={searchBarRef}
                      tableId={tableId}
                    />

                    <div className="dashboard-table-body">
                      <TableComponent
                        key={`${currentPage}-${pageSize}-${tableData?.totalItems || 0}`}
                        tableData={tableData?.data}
                        tableCount={tableCount[activeTabKey] || 0}
                        allItemsCount={allSiteDocumentCount}
                        refetch={refetch}
                        isLoading={isLoading || isFetching}
                        isDocumentsTab={isDocumentsTab}
                        isFavouriteTab={isFavouriteTab}
                        isCartTab={isCartTab}
                        tableRef={tableRef}
                        getDashboardTableHeight={sideView || !showFilterPanel ? getDashboardTableHeight : getTopViewDashboardTableHeight}
                        isThumbnailView={isThumbnailView}
                        resetSearchValues={resetSearchValues}
                        updateTotalDocumentCount={updateTotalDocumentCount}
                        width={width}
                        queryFilters={combinedFilters}
                      />
                    </div>
                    <div className="dashboard-table-footer">
                      <Card className="dashboard-card-footer">
                        <Col className="footer-col-actions">
                          <DashboardTableActions
                            tableActionsRef={tableActionsRef}
                            width={width}
                            className="col-6"
                            currentTable={
                              isCartTab ? "cartManagementTable" : undefined
                            }
                            showMoreActions={true}
                            isCartTab={isCartTab}
                            showRemoveFromFavourites={isFavouriteTab}
                            updateTotalDocumentCount={updateTotalDocumentCount}
                            refetch={() => refetch({ ...combinedFilters })}
                            filters={combinedFilters}
                            forceRender={isTourOpen}
                          />
                        </Col>
                        <Col className="footer-col-pagination">
                          {width > 765 &&
                            <PageSizeSelector
                              tableId={tableId}
                              tableDataCount={tableCount[activeTabKey] || 0}
                            />
                          }
                          <Pagination
                            simple
                            defaultCurrent={1}
                            current={currentPage}
                            pageSize={pageSize}
                            showSizeChanger={false}
                            showQuickJumper={false}
                            total={tableCount[activeTabKey] || 0}
                            hideOnSinglePage={true}
                            onChange={(page) =>
                              setCurrentPage(isCartTab, page)
                            }
                          />
                        </Col>
                      </Card>
                    </div>
                  </div>
                </Card>
              </div>
            </Col>
            <Col span={5} className="dashboard-document-tab-content">
              <FilterComponent
                filterActionRef={sideView ? filterActionRef : null}
                updateTotalDocumentCount={updateTotalDocumentCount}
                isDocumentsTab={isDocumentsTab}
                isCartTab={isCartTab}
                showFilterPanel={showFilterPanel}
                filterRef={sideView ? filterRef : null}
                resetSearchValues={resetSearchValues}
                handleFilterSearch={handleSearch}
                mobileView={mobileView}
                showFilterDrawer={showFilterDrawer}
                selectedItems={selectedItems}
                activeTabKey={activeTabKey}
                pageSize={pageSize}
                columnOptions={columnOptions}
                selectedColumns={columnOptions?.filter((x) => x.isEnabled)}
                setEnabledColumns={setEnabledColumns}
                onColumnClearment={handleResetColumns}
                onReorder={onReorder}
                isThumbnailView={isThumbnailView}
                tableViewVisible={true}
                isShowColumnsSelector={true}
                setLargeTableView={setLargeTableView}
                setThumbnailTableView={setThumbnailTableView}
                ddvSearch={ddvSearch}
                setDdvSearch={setDdvSearch}
                setAltSearch={setDescriptionSearch}
                closeFilterDrawer={() => handleFilterDrawerVisible(false)}
                descriptionSearch={descriptionSearch}
                tableCount={tableCount[activeTabKey] || 0}
                tableId={tableId}
              />
            </Col>
            <Drawer
              placement={sideView ? "right" : "top"}
              forceRender
              closable={false}
              styles={{body: {overflow: "hidden"}}}
              //width={360}
              className={!sideView ? "top-view-table" : "side-view-table"}
              onClose={() => handleFilterDrawerVisible(false)}
              open={filterDrawerOpen}
            >
              {showFilterDrawer && filterDrawerOpen && (
                <>
                  {isCartTab && selectedItems.length > 0 && (
                    <CartFilter
                      table="cartManagementTable"
                      mobileView={mobileView}
                      showFilterDrawer={showFilterDrawer}
                      width={width}
                      closeFilterDrawer={() =>
                        handleFilterDrawerVisible(false)
                      }
                    />
                  )}
                  {(isDocumentsTab || isFavouriteTab) && (
                    <DocumentFiltersContainer
                      key="documentFilters"
                      table="dashboardTable"
                      mobileView={mobileView}
                      showFilterDrawer={showFilterDrawer}
                      resetSearch={resetSearchValues}
                      handleSearch={handleSearch}
                      closeFilterDrawer={() =>
                        handleFilterDrawerVisible(false)
                      }
                      isThumbnailView={isThumbnailView}
                      isDocumentsTab={isDocumentsTab}
                      tableViewVisible={true}
                      setLargeTableView={setLargeTableView}
                      setThumbnailTableView={setThumbnailTableView}
                      isCartTab={isCartTab}
                      width={width}
                      ddvSearch={ddvSearch}
                      setDdvSearch={setDdvSearch}
                      setDescriptionSearch={setDescriptionSearch}
                      descriptionSearch={descriptionSearch}
                      searchBarRef={searchBarRef}
                      tableCount={tableCount[activeTabKey] || 0}
                    />
                  )}
                  {isCartTab && selectedItems.length === 0 && (
                    <DocumentFiltersContainer
                      table="cartManagementTable"
                      includeCart
                      mobileView={mobileView}
                      showFilterDrawer={showFilterDrawer}
                      resetSearch={resetSearchValues}
                      handleSearch={handleSearch}
                      closeFilterDrawer={() => handleFilterDrawerVisible(false)}
                      isThumbnailView={isThumbnailView}
                      isDocumentsTab={isDocumentsTab}
                      tableViewVisible={true}
                      setLargeTableView={setLargeTableView}
                      setThumbnailTableView={setThumbnailTableView}
                      isCartTab={isCartTab}
                      width={width}
                      ddvSearch={ddvSearch}
                      setDdvSearch={setDdvSearch}
                      setAltSearch={setDescriptionSearch}
                      setDescriptionSearch={setDescriptionSearch}
                      descriptionSearch={descriptionSearch}
                    />
                  )}
                </>
              )}
            </Drawer>
          </DashboardTabs>
        </Row>
      </div>
      <FormProvider>
        <PanelContainer
          id={panels.dashboard}
          title={`Document Detail: ${documentNumber?.toUpperCase()}`}
          className="panel document-detail"
          content={
            <PanelLazyLoader
              component={DocumentDetailPage}
              refetch={() => {
                refetch();
                dispatch(getDashboardFilters(combinedFilters, isCartTab ? "cartDashboard" : "dashboardTable", true));
              }}
              updateTotalDocumentCount={updateTotalDocumentCount}
              tableId={isCartTab ? "cartDashboard" : "dashboardTable"}
            />
          }
        />
      </FormProvider>
      <FormProvider>
        <PanelContainer
          id={panels.pdfPreview}
          title={`Document Preview: ${pdfPreviewDocNum.toUpperCase()}`}
          className="panel preview-pdf-panel"
          content={<PanelLazyLoader component={PreviewPdf} />}
        />
      </FormProvider>
      <FormProvider>
        <PanelContainer
          title="Create new job"
          className="panel job-detail"
          id={panels.jobCreate}
          content={
            <PanelLazyLoader
              component={CreateJobSummary}
              documentSelected={true}
              tableId={["dashboardTable"]}
              documents={selectedItems}
              document={item}
            />
          }
        />
      </FormProvider>
      <PanelContainer
        id={panels.createWorkpack}
        title={`Collection Creation Tool`}
        className="panel"
        content={
          <PanelLazyLoader
            component={CreateWorkpackPanel}
            siteId={currentSite?.siteId ?? currentSiteId}
            selectedItems={selectedItems}
            siteFullName={currentSite?.siteFullName || ""}
            accountId={currentSite?.accountId ?? null}
            accountName={currentSite?.accountName || ""}
          />
        }
      />
      <FormProvider>
        <PanelContainer
          id={panels.changeCategory}
          title="Change Categories"
          content={
            <PanelLazyLoader
              component={UpdateCategoriesPanel}
              tableId={isCartTab ? "cartDashboard" : "dashboardTable"}
              refetch={refetch}
              selectedItems={selectedItems}
            />
          }
        />
      </FormProvider>
      <FormProvider>
        <PanelContainer
          id={panels.changeSites}
          title="Change Sites"
          content={
            <PanelLazyLoader
              component={ChangeSitesPanel}
              currentSiteId={currentSiteId}
              refetch={refetch}
              selectedItems={selectedItems}
            />
          }
        />
      </FormProvider>
      <FormProvider>
        <PanelContainer
          id={panels.showChangeComment}
          title={`Show change comment`}
          zIndex={3}
          pageUrl={`document/${item.documentId}`}
          content={
            <PanelLazyLoader
              component={DocumentCommentPanel}
              refetch={refetch}
              permissions={permissions}
            />
          }
        />
      </FormProvider>
      <FormProvider>
        <PanelContainer
          id={panels.addChangeComment}
          title="Add Change Comment(s)"
          zIndex={3}
          content={
            <PanelLazyLoader
              component={UnderChangeComment}
              refetch={refetch}
              tableId={isCartTab ? "cartDashboard" : "dashboardTable"}
              selectedItems={selectedItems}
            />
          }
        />
      </FormProvider>
      <FormProvider>
        <PanelContainer
          id={panels.retireDocument}
          title="Retire Document / Drawing"
          zIndex={3}
          content={
            <PanelLazyLoader
              component={RetireDocumentPanel}
              refetch={refetch}
              tableId={isCartTab ? "cartDashboard" : "dashboardTable"}
              selectedItems={selectedItems}
            />
          }
        />
      </FormProvider>
      <FormProvider>
        <PanelContainer
          id={panels.rollbackDocument}
          title="Rollback Document / Drawing"
          zIndex={3}
          content={
            <PanelLazyLoader
              component={RollbackConfirm}
              refetch={refetch}
              multiple={true}
              tableId={isCartTab ? "cartDashboard" : "dashboardTable"}
              selectedItems={selectedItems}
            />
          }
        />
      </FormProvider>
      <FormProvider>
        <PanelContainer
          id={panels.deleteDocument}
          title={`Delete ${
            showAllRevision ? "Revision(s)" : "Document / Drawing(s)"
          }`}
          zIndex={3}
          content={
            <PanelLazyLoader
              component={DeleteDocumentPanel}
              refetch={refetch}
              tableId={isCartTab ? "cartDashboard" : "dashboardTable"}
              selectedItems={selectedItems}
            />
          }
        />
      </FormProvider>
      <FormProvider>
        <PanelContainer
          id={panels.siteUser}
          title={"View User Details"}
          content={
            <PanelLazyLoader
              component={UserProfile}
              panelId={panels.systemUser}
              readonly
            />
          }
          closable={false}
          className={"panel document-detail"}
        />
      </FormProvider>
    </Layout>
  );
};

export default Dashboard;
